import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppThunk } from '../store'
import { setErrors } from '../common/errorsSlice'
import {
  HolidaySchedule,
  RequestDeleteHolidaySchedule,
  RequestGetHolidayScheduleDetail,
  RequestPatchHolidaySchedule,
  RequestPostHolidaySchedule,
  ResponseGetHolidaySchedule,
  ResponseGetHolidayScheduleDetail,
  ResponsePostHolidaySchedule,
} from '../../types/holidaySchedule'
import {
  deleteHolidaySchedule,
  getHolidaySchedule,
  getHolidayScheduleDetail,
  patchHolidaySchedule,
  postHolidaySchedule,
} from '../../apis/holidaySchedule'
import { trimStart } from 'lodash'

interface HolidayScheduleState {
  holidaySchedules: HolidaySchedule[] | null
  isLoading: boolean
}

const initialState: HolidayScheduleState = {
  holidaySchedules: null,
  isLoading: true,
}

const holidayScheduleStateSlice = createSlice({
  name: 'holidaySchedule',
  initialState,
  reducers: {
    requestStart: (state) => {
      state.isLoading = true
      return state
    },
    buildHolidaySchedule: (state, action: PayloadAction<ResponseGetHolidaySchedule>) => {
      state.holidaySchedules = action.payload.holiday_schedules.map((v) => ({
        ...v,
        holiday_enabled: false,
        holidays: [],
        base_work_start_time: null,
        base_work_end_time: null,
        base_break_time: 0,
        scheduled_break_times: [],
        use_common_work_time: false,
        use_common_holiday_list: false,
        use_common_scheduled_break_times: false,
      }))
      state.isLoading = false
      return state
    },
    buildHolidayScheduleDetail: (state, action: PayloadAction<ResponseGetHolidayScheduleDetail>) => {
      state.holidaySchedules = state.holidaySchedules?.map((v) => (
        v.id === action.payload.holiday_schedule.id ? action.payload.holiday_schedule : v
      )) || []
      state.isLoading = false
      return state
    },
    addHolidaySchedule: (state, action: PayloadAction<ResponsePostHolidaySchedule>) => {
      state.isLoading = false
      state.holidaySchedules?.push(action.payload.holiday_schedule)
      return state
    },
    modHolidaySchedule: (state, action: PayloadAction<RequestPatchHolidaySchedule>) => {
      state.isLoading = false
      if (!state.holidaySchedules) return state
      state.holidaySchedules = state.holidaySchedules.map((v) => (v.id === action.payload.id
        ? {
          id: v.id,
          name: action.payload.name,
          member_ids: action.payload.member_ids === undefined
            ? v.member_ids
            : action.payload.member_ids,
          holiday_enabled: action.payload.holiday_enabled === undefined
            ? v.holiday_enabled
            : action.payload.holiday_enabled,
          holidays: v.holidays,
          common: v.common,
          base_work_start_time: action.payload.base_work_start_time === undefined
            ? v.base_work_start_time
            : (trimStart(action.payload.base_work_start_time || "", "0") || null),
          base_work_end_time: action.payload.base_work_end_time === undefined
            ? v.base_work_end_time
            : (trimStart(action.payload.base_work_end_time || "", "0") || null),
          base_break_time: action.payload.base_break_time === undefined
            ? v.base_break_time
            : action.payload.base_break_time,
          scheduled_break_times: action.payload.scheduled_break_times || [],
          use_common_work_time: action.payload.use_common_work_time === undefined
            ? v.use_common_work_time
            : action.payload.use_common_work_time,
          use_common_holiday_list: action.payload.use_common_holiday_list === undefined
            ? v.use_common_holiday_list
            : action.payload.use_common_holiday_list,
          use_common_scheduled_break_times: action.payload.use_common_scheduled_break_times === undefined
            ? v.use_common_scheduled_break_times
            : action.payload.use_common_scheduled_break_times,
        }
        : v
      )) || []
      return state
    },
    delHolidaySchedule: (state, action: PayloadAction<RequestDeleteHolidaySchedule>) => {
      state.isLoading = false
      if (!state.holidaySchedules) return state
      state.holidaySchedules = state.holidaySchedules.filter(
        (v) => v.id !== Number(action.payload.id),
      )
      return state
    },
    requestFailure: (state) => {
      state.isLoading = false
      return state
    },
  },
  extraReducers: (builder) => {
    builder.addCase('logout', () => initialState)
  },
})

export const {
  requestStart,
  buildHolidaySchedule,
  buildHolidayScheduleDetail,
  addHolidaySchedule,
  modHolidaySchedule,
  delHolidaySchedule,
  requestFailure,
} = holidayScheduleStateSlice.actions

export const holidayScheduleReducer = holidayScheduleStateSlice.reducer

export const fetchHolidaySchedule = (
  callback?: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getHolidaySchedule()
    .then((result) => {
      dispatch(buildHolidaySchedule(result))
      if (callback) {
        callback()
      }
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const fetchHolidayScheduleDetail = (
  params: RequestGetHolidayScheduleDetail,
  callback?: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  getHolidayScheduleDetail(params)
    .then((result) => {
      dispatch(buildHolidayScheduleDetail(result))
      if (callback) {
        callback()
      }
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const createHolidaySchedule = (
  params: RequestPostHolidaySchedule,
  callback?: (result: ResponsePostHolidaySchedule) => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  postHolidaySchedule(params)
    .then((result) => {
      dispatch(addHolidaySchedule(result))
      if (callback) {
        callback(result)
      }
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const updateHolidaySchedule = (
  params: RequestPatchHolidaySchedule,
  callback?: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  patchHolidaySchedule(params)
    .then(() => {
      dispatch(modHolidaySchedule(params))
      if (callback) {
        callback()
      }
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}

export const destroyHolidaySchedule = (
  params: RequestDeleteHolidaySchedule,
  callback?: () => void,
): AppThunk => async (dispatch) => {
  dispatch(requestStart())
  deleteHolidaySchedule(params)
    .then(() => {
      dispatch(delHolidaySchedule(params))
      if (callback) {
        callback()
      }
    })
    .catch((error) => {
      dispatch(requestFailure())
      dispatch(setErrors(error))
    })
}
